import { createHttpClient, type CustomerAuth, type HttpClient } from './http';
import { createWebSocketClient, type ConnectionState, type WebSocketClient } from './websocket';
import { ChatClientError, ChatHttpError } from './errors';
import type {
  ChatAttachment,
  ChatAttachmentUrl,
  ChatConversation,
  ChatCarryOverOffer,
  ChatMessage,
  ChatReaction,
  CustomerProfile,
  SendMessageRequest,
} from './entity';

export interface ChatClientConfig {
  httpBaseUrl: string;
  webSocketBaseUrl: string;
  // Present only when the customer is known through the Flix Identity
  // bridge — see CustomerAuth in ./http.
  customerAuth?: CustomerAuth;
}

// The Bearer token this SDK obtains from createConversation/reopenConversation
// is deliberately kept out of this public interface entirely — every method
// here has the exact same shape it always has. Authentication is this SDK's
// own internal concern; the widget never sees a token.
export interface ChatClient {
  createConversation(customerId: string): Promise<ChatConversation>;
  getConversation(conversationId: string): Promise<ChatConversation>;
  listConversations(): Promise<ChatConversation[]>;
  listMessages(conversationId: string): Promise<ChatMessage[]>;
  getConversationHistory(conversationId: string): Promise<ChatMessage[]>;
  sendMessage(request: SendMessageRequest): Promise<ChatMessage>;
  uploadAttachment(
    conversationId: string,
    file: Blob,
    filename: string,
    onProgress?: (fraction: number) => void,
  ): Promise<ChatAttachment>;
  downloadAttachment(fileId: string): Promise<Blob>;
  getAttachmentUrl(fileId: string): Promise<ChatAttachmentUrl>;
  addReaction(messageId: string, emoji: string): Promise<ChatReaction[]>;
  removeReaction(messageId: string, emoji: string): Promise<ChatReaction[]>;
  reportMessage(messageId: string): Promise<void>;
  // "Send again" for the customer's own message that did not reach support.
  resendMessage(messageId: string): Promise<ChatMessage>;
  // Messages from a previous chat whose support channel was deleted that
  // never reached support, and sending one of them into this chat.
  listCarryOverOffers(): Promise<ChatCarryOverOffer[]>;
  carryOverMessage(messageId: string): Promise<ChatMessage | null>;
  reopenConversation(conversationId: string): Promise<ChatConversation>;
  closeConversation(conversationId: string): Promise<ChatConversation>;
  getCustomerProfile(): Promise<CustomerProfile>;
  setCustomerName(name: string): Promise<CustomerProfile>;

  connect(customerId: string, conversationId?: string): void;
  disconnect(): void;
  getConnectionState(): ConnectionState;
  onConnectionStateChange(handler: (state: ConnectionState) => void): () => void;
  subscribe(handler: (payload: unknown) => void): () => void;
  // Best-effort, fire-and-forget — a typing signal is never worth surfacing
  // an error for (no active connection, socket mid-reconnect, etc. all just
  // silently no-op). conversationId is carried for symmetry with the rest
  // of this interface; the server's own per-connection identity is what
  // actually gets trusted.
  sendTyping(conversationId: string): void;
}

function buildWebSocketUrl(
  baseUrl: string,
  customerId: string,
  conversationId: string | undefined,
  token: string,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('customerId', customerId);
  url.searchParams.set('token', token);

  if (conversationId) {
    url.searchParams.set('conversationId', conversationId);
  }

  return url.toString();
}

function isAuthError(error: unknown): boolean {
  return error instanceof ChatHttpError && error.status === 401;
}

export function createChatClient(config: ChatClientConfig): ChatClient {
  const http: HttpClient = createHttpClient({
    baseUrl: config.httpBaseUrl,
    customerAuth: config.customerAuth,
  });
  let webSocket: WebSocketClient | null = null;
  const subscribedMessageHandlers = new Set<(payload: unknown) => void>();
  const subscribedStateHandlers = new Set<(state: ConnectionState) => void>();

  // Session state is entirely internal — never exposed on the public
  // ChatClient interface above.
  let currentToken: string | null = null;
  let currentCustomerId: string | null = null;

  async function refreshSession(): Promise<string> {
    if (!currentCustomerId) {
      throw new ChatClientError('Cannot authenticate: no active conversation session.');
    }

    const session = await http.createConversation(currentCustomerId);
    currentToken = session.token;

    return session.token;
  }

  function requireToken(): string {
    if (!currentToken) {
      throw new ChatClientError('Not connected: call createConversation first.');
    }

    return currentToken;
  }

  // Every authenticated call goes through this: if the current token is
  // rejected as expired/invalid (401), it's refreshed exactly once via the
  // same bootstrap endpoint that originally issued it, and the call is
  // retried with the new token — a customer actively chatting never sees
  // an auth error just because their token's TTL elapsed mid-session.
  async function withAuth<T>(fn: (token: string) => Promise<T>): Promise<T> {
    const token = requireToken();

    try {
      return await fn(token);
    } catch (error) {
      if (!isAuthError(error)) {
        throw error;
      }

      const refreshedToken = await refreshSession();
      return fn(refreshedToken);
    }
  }

  return {
    async createConversation(customerId) {
      currentCustomerId = customerId;
      const session = await http.createConversation(customerId);
      currentToken = session.token;

      return session;
    },

    getConversation(conversationId) {
      return withAuth((token) => http.getConversation(conversationId, token));
    },

    listConversations() {
      return http.listConversations();
    },

    listMessages(conversationId) {
      return withAuth((token) => http.listMessages(conversationId, token));
    },

    getConversationHistory(conversationId) {
      return http.getConversationHistory(conversationId);
    },

    getCustomerProfile() {
      return http.getCustomerProfile();
    },

    setCustomerName(name) {
      return http.setCustomerName(name);
    },

    sendMessage(request) {
      return withAuth((token) => http.sendMessage(request, token));
    },

    uploadAttachment(conversationId, file, filename, onProgress) {
      return withAuth((token) =>
        http.uploadAttachment(conversationId, file, filename, token, onProgress),
      );
    },

    downloadAttachment(fileId) {
      return withAuth((token) => http.downloadAttachment(fileId, token));
    },

    getAttachmentUrl(fileId) {
      return withAuth((token) => http.getAttachmentUrl(fileId, token));
    },

    addReaction(messageId, emoji) {
      return withAuth((token) => http.addReaction(messageId, emoji, token));
    },

    removeReaction(messageId, emoji) {
      return withAuth((token) => http.removeReaction(messageId, emoji, token));
    },

    reportMessage(messageId) {
      return withAuth((token) => http.reportMessage(messageId, token));
    },

    resendMessage(messageId) {
      return withAuth((token) => http.resendMessage(messageId, token));
    },

    listCarryOverOffers() {
      return withAuth((token) => http.listCarryOverOffers(token));
    },

    carryOverMessage(messageId) {
      return withAuth((token) => http.carryOverMessage(messageId, token));
    },

    async reopenConversation(conversationId) {
      const session = await withAuth((token) => http.reopenConversation(conversationId, token));
      currentToken = session.token;

      return session;
    },

    async closeConversation(conversationId) {
      const conversation = await withAuth((token) => http.closeConversation(conversationId, token));

      // The token this session was using is now revoked server-side (closing
      // a conversation revokes its tokens) — clearing it here means any
      // stray authenticated call fails fast with the same clear error
      // createConversation would need to be called again, rather than
      // surfacing a confusing 401 from the server.
      currentToken = null;

      return conversation;
    },

    connect(customerId, conversationId) {
      if (webSocket) {
        return;
      }

      const token = requireToken();
      const url = buildWebSocketUrl(config.webSocketBaseUrl, customerId, conversationId, token);
      webSocket = createWebSocketClient({ url });

      for (const handler of subscribedMessageHandlers) {
        webSocket.onMessage(handler);
      }

      for (const handler of subscribedStateHandlers) {
        webSocket.onStateChange(handler);
      }

      webSocket.connect();
    },

    disconnect() {
      webSocket?.disconnect();
      webSocket = null;
    },

    getConnectionState() {
      return webSocket?.getState() ?? 'idle';
    },

    onConnectionStateChange(handler) {
      subscribedStateHandlers.add(handler);
      webSocket?.onStateChange(handler);

      return () => {
        subscribedStateHandlers.delete(handler);
        webSocket?.offStateChange(handler);
      };
    },

    subscribe(handler) {
      subscribedMessageHandlers.add(handler);
      webSocket?.onMessage(handler);

      return () => {
        subscribedMessageHandlers.delete(handler);
        webSocket?.offMessage(handler);
      };
    },

    sendTyping(conversationId) {
      try {
        webSocket?.send({ type: 'typing', conversationId });
      } catch {
        // No open connection right now — nothing worth surfacing.
      }
    },
  };
}
